import { array, nonEmptyArray, option, record } from 'fp-ts';
import { identity, pipe } from 'fp-ts/function';
import { Rate, RateResult, useRepositoryQuery } from '@mews-commander/data-access';
import { WithEnterpriseId, WithServiceId, Option } from '@mews-commander/types';
import { createOption } from '@mews-commander/ui';
import { LocalizedProperty } from '@mews/framework';

interface Params extends WithServiceId, WithEnterpriseId {
    convertRateToOption?: (rate: Rate) => Option;
    filterRate?: (rate: Rate) => boolean;
    enabled?: boolean;
}

export const useGroupedRatesOptions = ({
    enterpriseId,
    serviceId,
    filterRate,
    convertRateToOption = defaultConvertRateToOption,
    enabled = true,
}: Params) => {
    const ratesResult = useRepositoryQuery(
        'rates',
        'getAll',
        {
            serviceIds: [serviceId],
            enterpriseIds: [enterpriseId],
        },
        {
            select: ({ rateGroups, rates }: RateResult) => {
                if (rates && array.isNonEmpty(rates) && rateGroups) {
                    const groupedRateOptions = pipe(
                        rates,
                        filterRate ? array.filter(filterRate) : identity,
                        nonEmptyArray.groupBy((rate: Rate) => rate.rateGroupId),
                        record.map(array.map(convertRateToOption))
                    );

                    return pipe(
                        rateGroups,
                        array.filterMap(({ id, names }) =>
                            groupedRateOptions[id]
                                ? option.some({
                                      groupLabel: <LocalizedProperty property={names} />,
                                      groupOptions: groupedRateOptions[id],
                                  })
                                : option.none
                        )
                    );
                }
                return [];
            },
            enabled,
        }
    );
    return ratesResult;
};

const defaultConvertRateToOption = (rate: Rate): Option =>
    createOption({
        value: rate.id,
        label: rate.name,
    });
